import { useState, useCallback } from 'react'
import { Box, Button, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { FormContainer, TextFieldElement } from 'react-hook-form-mui'
import { resetPassword } from 'src/utils/auth'
import backgroundImage from 'src/images/background/sign_background.svg'

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': { border: 'none', borderBottom: '1px solid #e2e8f0' },
    '&:hover fieldset': { borderBottom: '1px solid #cbd5e1' },
    '&.Mui-focused fieldset': { border: 'none', borderBottom: '2px solid #4277b9' }
  },
  '& .MuiInputLabel-root': { fontSize: '0.9rem', color: '#94a3b8', '&.Mui-focused': { color: '#4277b9' } }
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string>()
  const formContext = useForm<{ email: string }>({ defaultValues: { email: '' } })

  const onSubmit = useCallback(async (data: { email: string }) => {
    setError(undefined)
    try {
      await resetPassword(data.email)
      setSent(true)
    } catch {
      setError("Impossible d'envoyer l'email. Vérifiez l'adresse.")
    }
  }, [])

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left */}
      <Box
        sx={{
          flex: '1 1 50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 5 },
          py: 4,
          backgroundColor: 'white'
        }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#4277b9', mb: 6, textAlign: 'center' }}>
            BatiKal
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
            Mot de passe oublié
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
            Entrez votre email pour recevoir un lien de réinitialisation.
          </Typography>

          {sent ? (
            <>
              <Box sx={{ p: 2, borderRadius: '8px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', mb: 2.5 }}>
                <Typography variant="body2" sx={{ color: '#166534' }}>
                  Un lien a été envoyé à votre adresse email.
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                disableElevation
                sx={{ py: 1.5, borderRadius: '24px', fontWeight: 600, textTransform: 'none', backgroundColor: '#4277b9', '&:hover': { backgroundColor: '#3666a3' } }}>
                Retour à la connexion
              </Button>
            </>
          ) : (
            <FormContainer formContext={formContext} onSuccess={onSubmit}>
              <TextFieldElement name="email" placeholder="Adresse email" type="email" fullWidth required sx={{ ...fieldSx, mb: 3 }} variant="outlined" />
              {error && <Typography variant="body2" sx={{ color: '#dc2626', mb: 1.5, fontSize: '0.85rem' }}>{error}</Typography>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disableElevation
                sx={{ py: 1.5, borderRadius: '24px', fontWeight: 600, textTransform: 'none', backgroundColor: '#4277b9', '&:hover': { backgroundColor: '#3666a3' } }}>
                Envoyer le lien
              </Button>
            </FormContainer>
          )}

          <Typography variant="body2" sx={{ mt: 3, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" sx={{ color: '#4277b9', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Retour à la connexion
            </Link>
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: '#cbd5e1', mt: 'auto', pt: 4 }}>
          &copy; BatiKal {new Date().getFullYear()}
        </Typography>
      </Box>

      {/* Right */}
      <Box
        sx={{
          flex: '1 1 50%',
          display: { xs: 'none', md: 'block' },
          background: `url(${backgroundImage}) center / cover no-repeat`
        }}
      />
    </Box>
  )
}
